import React, { useState, useEffect, useRef } from 'react';
import './Desktop.css';
import ContextMenu from './ContextMenu';
import ConfirmDialog from './ConfirmDialog';

const Desktop = ({ children, onDesktopClick, onOpenApp, onContextMenu, deletedIcons = [], onDeleteIcons, onResetPositions, resetTrigger }) => {
  const [contextMenu, setContextMenu] = useState(null);
  const [draggedIcon, setDraggedIcon] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);
  const [mouseDownPos, setMouseDownPos] = useState({ x: 0, y: 0 });
  const [selectedIcons, setSelectedIcons] = useState([]);
  const [selectionBox, setSelectionBox] = useState(null);
  const [isSelectingBox, setIsSelectingBox] = useState(false);
  const [isOverRecycleBin, setIsOverRecycleBin] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const desktopRef = useRef(null);
  const recycleBinRef = useRef(null);

  // Default positions for desktop icons
  const defaultPositions = {
    'home': { top: 20, left: 20 },
    'about': { top: 20, left: 120 },
    'services': { top: 130, left: 20 },
    'contact': { top: 130, left: 120 },
    'spotify': { top: 240, left: 20 }, // Middle left - between contact and minesweeper
    'calculator': { top: 20, right: 120 },
    'notepad': { top: 20, right: 20 },
    'file-manager': { top: 130, right: 120 },
    'clock': { top: 130, right: 20 },
    'minesweeper': { bottom: 162, left: 20 },
    'solitaire': { bottom: 162, left: 120 },
    'paint': { bottom: 52, left: 20 },
    'media-player': { bottom: 52, left: 120 },
    'recycle-bin': { bottom: 162, right: 120 },
    'control-panel': { bottom: 162, right: 20 },
    'ms-dos': { bottom: 52, right: 120 },
  };

  const desktopApps = [
    // Sol Üst - Ana Menü
    { id: 'home', name: 'Home', icon: '/assets/icons/computer.png' },
    { id: 'about', name: 'About Me', icon: '/assets/icons/documents.png' },
    { id: 'services', name: 'Services', icon: '/assets/icons/folder.png' },
    { id: 'contact', name: 'Contact Me', icon: '/assets/icons/mail.png' },

    // Sağ Üst - Tools
    { id: 'calculator', name: 'Calculator', icon: '/assets/icons/calculator.png' },
    { id: 'notepad', name: 'Notepad', icon: '/assets/icons/notepad.png' },
    { id: 'file-manager', name: 'File Manager', icon: '/assets/icons/file-manager.png' },
    { id: 'clock', name: '3D Van', icon: '/assets/icons/clock.png' },

    // Sol Alt - Fun Stuff
    { id: 'minesweeper', name: 'Minesweeper', icon: '/assets/icons/minesweeper.png' },
    { id: 'solitaire', name: 'Solitaire', icon: '/assets/icons/solitaire.png' },
    { id: 'paint', name: 'Paint', icon: '/assets/icons/paint.png' },
    { id: 'media-player', name: 'Media Player', icon: '/assets/icons/media-player.png' },

    // Sağ Alt - System
    { id: 'recycle-bin', name: 'Recycle Bin', icon: '/assets/icons/recycle-bin-empty.png', iconFull: '/assets/icons/recycle-bin-full.png' },
    { id: 'control-panel', name: 'Settings', icon: '/assets/icons/control-panel.png' },
    { id: 'ms-dos', name: 'MS-DOS', icon: '/assets/icons/ms-dos.png' },
    { id: 'spotify', name: 'Spotify', icon: '/assets/icons/compact-disc.png' },
  ];

  // Load icon positions from localStorage or use defaults
  const [iconPositions, setIconPositions] = useState(() => {
    const saved = localStorage.getItem('desktopIconPositions');
    return saved ? JSON.parse(saved) : defaultPositions;
  });

  // Save positions to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('desktopIconPositions', JSON.stringify(iconPositions));
  }, [iconPositions]);

  // Reset positions when resetTrigger changes
  useEffect(() => {
    if (resetTrigger > 0) {
      setIconPositions(defaultPositions);
    }
  }, [resetTrigger]);

  // Drag handlers
  const handleMouseDown = (e, app) => {
    // Left click only for dragging
    if (e.button !== 0) return;

    e.preventDefault();
    e.stopPropagation();

    const iconElement = e.currentTarget;
    const rect = iconElement.getBoundingClientRect();

    // Ctrl+Click toggles selection
    if (e.ctrlKey) {
      if (selectedIcons.includes(app.id)) {
        setSelectedIcons(selectedIcons.filter(id => id !== app.id));
      } else {
        setSelectedIcons([...selectedIcons, app.id]);
      }
      return;
    }

    // If clicking on a selected icon, drag all selected icons
    if (selectedIcons.includes(app.id)) {
      setDraggedIcon(app.id);
    } else {
      // Otherwise, deselect all and drag just this icon
      setSelectedIcons([]);
      setDraggedIcon(app.id);
    }

    setHasMoved(false);
    setMouseDownPos({ x: e.clientX, y: e.clientY });
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (!draggedIcon || !desktopRef.current) return;

    // Calculate distance moved
    const dx = e.clientX - mouseDownPos.x;
    const dy = e.clientY - mouseDownPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Only start dragging if moved more than 5px (threshold)
    if (distance > 5) {
      setHasMoved(true);
    }

    // Check if dragging over Recycle Bin (with some tolerance)
    if (hasMoved) {
      if (recycleBinRef.current) {
        const recycleBinRect = recycleBinRef.current.getBoundingClientRect();
        const tolerance = 20; // Add 20px tolerance around the icon
        const isOver = e.clientX >= (recycleBinRect.left - tolerance) &&
                       e.clientX <= (recycleBinRect.right + tolerance) &&
                       e.clientY >= (recycleBinRect.top - tolerance) &&
                       e.clientY <= (recycleBinRect.bottom + tolerance);
        setIsOverRecycleBin(isOver);
      } else {
        setIsOverRecycleBin(false);
      }
    }

    // Only update position if actually dragging (moved past threshold)
    if (hasMoved || distance > 5) {
      e.preventDefault();

      const desktopRect = desktopRef.current.getBoundingClientRect();
      const newLeft = e.clientX - desktopRect.left - dragOffset.x;
      const newTop = e.clientY - desktopRect.top - dragOffset.y;

      // Constrain to desktop bounds
      const constrainedLeft = Math.max(0, Math.min(newLeft, desktopRect.width - 90));
      const constrainedTop = Math.max(0, Math.min(newTop, desktopRect.height - 100));

      // If multiple icons are selected, move them all together
      if (selectedIcons.length > 0 && selectedIcons.includes(draggedIcon)) {
        setIconPositions(prev => {
          const newPositions = { ...prev };

          // Get current dragged icon position (convert right/bottom to left/top if needed)
          const draggedPos = prev[draggedIcon];
          let draggedLeft = draggedPos.left;
          if (draggedLeft === undefined && draggedPos.right !== undefined) {
            draggedLeft = desktopRect.width - draggedPos.right - 90;
          }

          const deltaX = constrainedLeft - draggedLeft;
          const deltaY = constrainedTop - (draggedPos.top || 0);

          // Move all selected icons by the same delta
          selectedIcons.forEach(iconId => {
            const currentPos = prev[iconId];

            // Convert right/bottom to left/top if needed
            let currentLeft = currentPos.left;
            let currentTop = currentPos.top;

            if (currentLeft === undefined && currentPos.right !== undefined) {
              currentLeft = desktopRect.width - currentPos.right - 90;
            }
            if (currentTop === undefined && currentPos.bottom !== undefined) {
              currentTop = desktopRect.height - currentPos.bottom - 100;
            }

            newPositions[iconId] = {
              top: Math.max(0, Math.min((currentTop || 0) + deltaY, desktopRect.height - 100)),
              left: Math.max(0, Math.min((currentLeft || 0) + deltaX, desktopRect.width - 90))
            };
          });

          return newPositions;
        });
      } else {
        // Just move the single dragged icon
        setIconPositions(prev => ({
          ...prev,
          [draggedIcon]: {
            top: constrainedTop,
            left: constrainedLeft
          }
        }));
      }
    }
  };

  const handleMouseUp = (e, app) => {
    // Check if we're over recycle bin at mouseUp time
    let isCurrentlyOverRecycleBin = false;
    if (recycleBinRef.current && hasMoved) {
      const recycleBinRect = recycleBinRef.current.getBoundingClientRect();
      const tolerance = 20;
      isCurrentlyOverRecycleBin = e.clientX >= (recycleBinRect.left - tolerance) &&
                                   e.clientX <= (recycleBinRect.right + tolerance) &&
                                   e.clientY >= (recycleBinRect.top - tolerance) &&
                                   e.clientY <= (recycleBinRect.bottom + tolerance);
    }

    // If dropped over Recycle Bin, show confirmation
    if (hasMoved && isCurrentlyOverRecycleBin && draggedIcon && draggedIcon !== 'recycle-bin') {
      const iconsToDelete = selectedIcons.length > 0 && selectedIcons.includes(draggedIcon)
        ? selectedIcons.filter(id => id !== 'recycle-bin')
        : [draggedIcon];

      if (iconsToDelete.length > 0) {
        const message = iconsToDelete.length === 1
          ? `Are you sure you want to move "${desktopApps.find(a => a.id === iconsToDelete[0])?.name}" to Recycle Bin?`
          : `Are you sure you want to move ${iconsToDelete.length} items to Recycle Bin?`;

        setConfirmDialog({
          message,
          iconsToDelete
        });
      }
    }
    // If mouse didn't move (or moved very little), treat as click
    else if (!hasMoved && app) {
      onOpenApp(app);
    }

    setDraggedIcon(null);
    setHasMoved(false);
    setIsOverRecycleBin(false);
  };

  useEffect(() => {
    if (draggedIcon) {
      const handleGlobalMouseUp = (e) => {
        const app = desktopApps.find(a => a.id === draggedIcon);
        handleMouseUp(e, app);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [draggedIcon, dragOffset, hasMoved, mouseDownPos]);

  // Selection box event listeners
  useEffect(() => {
    if (isSelectingBox) {
      document.addEventListener('mousemove', handleSelectionBoxMove);
      document.addEventListener('mouseup', handleSelectionBoxEnd);
      return () => {
        document.removeEventListener('mousemove', handleSelectionBoxMove);
        document.removeEventListener('mouseup', handleSelectionBoxEnd);
      };
    }
  }, [isSelectingBox, selectionBox]);

  const handleRefreshDesktop = () => {
    setIconPositions(defaultPositions);
    setContextMenu(null);
  };

  const handleConfirmDelete = () => {
    if (confirmDialog && onDeleteIcons) {
      onDeleteIcons([...deletedIcons, ...confirmDialog.iconsToDelete]);
      setSelectedIcons([]);
      setConfirmDialog(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialog(null);
  };

  const handleDesktopContextMenu = (e) => {
    // Only show context menu if not clicking on an icon
    if (e.target.closest('.desktop-icon')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          label: 'Refresh',
          onClick: handleRefreshDesktop
        }
      ]
    });
  };

  const handleDesktopClick = (e) => {
    setContextMenu(null);
    // Clear selection when clicking on empty desktop
    if (e.target === desktopRef.current || e.target.closest('.desktop-icons')) {
      setSelectedIcons([]);
    }
    if (onDesktopClick) {
      onDesktopClick(e);
    }
  };

  // Desktop mousedown for selection box
  const handleDesktopMouseDown = (e) => {
    // Only start selection box on left click on empty space
    // Don't start if clicking on icon or window
    if (e.button !== 0 || e.target.closest('.desktop-icon') || e.target.closest('.window')) return;

    if (!e.ctrlKey) {
      setSelectedIcons([]);
    }

    setIsSelectingBox(true);
    setMouseDownPos({ x: e.clientX, y: e.clientY });
    setSelectionBox({
      startX: e.clientX,
      startY: e.clientY,
      currentX: e.clientX,
      currentY: e.clientY
    });
  };

  // Handle selection box drag
  const handleSelectionBoxMove = (e) => {
    if (!isSelectingBox) return;

    setSelectionBox(prev => ({
      ...prev,
      currentX: e.clientX,
      currentY: e.clientY
    }));

    // Check which icons intersect with selection box
    if (desktopRef.current) {
      const desktopRect = desktopRef.current.getBoundingClientRect();
      const boxLeft = Math.min(selectionBox.startX, e.clientX) - desktopRect.left;
      const boxRight = Math.max(selectionBox.startX, e.clientX) - desktopRect.left;
      const boxTop = Math.min(selectionBox.startY, e.clientY) - desktopRect.top;
      const boxBottom = Math.max(selectionBox.startY, e.clientY) - desktopRect.top;

      const selected = desktopApps.filter(app => {
        const pos = iconPositions[app.id];
        if (!pos) return false;

        // Calculate actual icon position
        let iconLeft, iconTop;
        if (pos.left !== undefined) {
          iconLeft = pos.left;
        } else if (pos.right !== undefined) {
          iconLeft = desktopRect.width - pos.right - 90;
        } else {
          return false;
        }

        if (pos.top !== undefined) {
          iconTop = pos.top;
        } else if (pos.bottom !== undefined) {
          iconTop = desktopRect.height - pos.bottom - 100;
        } else {
          return false;
        }

        const iconRight = iconLeft + 90;
        const iconBottom = iconTop + 100;

        return !(iconRight < boxLeft || iconLeft > boxRight ||
                 iconBottom < boxTop || iconTop > boxBottom);
      }).map(app => app.id);

      setSelectedIcons(selected);
    }
  };

  const handleSelectionBoxEnd = () => {
    setIsSelectingBox(false);
    setSelectionBox(null);
  };

  return (
    <div
      ref={desktopRef}
      className="desktop"
      onClick={handleDesktopClick}
      onMouseDown={handleDesktopMouseDown}
      onContextMenu={handleDesktopContextMenu}
    >
      <div className="desktop-icons">
        {desktopApps
          .filter(app => !deletedIcons.includes(app.id))
          .map((app) => {
            const position = iconPositions[app.id] || {};
            const style = {
              top: position.top !== undefined ? `${position.top}px` : undefined,
              left: position.left !== undefined ? `${position.left}px` : undefined,
              right: position.right !== undefined ? `${position.right}px` : undefined,
              bottom: position.bottom !== undefined ? `${position.bottom}px` : undefined,
              cursor: draggedIcon === app.id ? 'grabbing' : 'grab'
            };

            const isSelected = selectedIcons.includes(app.id);
            const isRecycleBin = app.id === 'recycle-bin';
            const isHighlighted = isOverRecycleBin && isRecycleBin && hasMoved;

            // Use full icon if Recycle Bin has items
            const iconSrc = isRecycleBin && deletedIcons.length > 0 && app.iconFull
              ? app.iconFull
              : app.icon;

            return (
              <div
                key={app.id}
                ref={isRecycleBin ? recycleBinRef : null}
                className={`desktop-icon ${draggedIcon === app.id ? 'dragging' : ''} ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                style={style}
                onMouseDown={(e) => handleMouseDown(e, app)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <div className="icon">
                  <img src={iconSrc} alt={app.name} />
                </div>
                <div className="icon-label">{app.name}</div>
              </div>
            );
          })}
      </div>
      <div className="desktop-windows" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
      {selectionBox && (
        <div
          className="selection-box"
          style={{
            left: Math.min(selectionBox.startX, selectionBox.currentX),
            top: Math.min(selectionBox.startY, selectionBox.currentY),
            width: Math.abs(selectionBox.currentX - selectionBox.startX),
            height: Math.abs(selectionBox.currentY - selectionBox.startY)
          }}
        />
      )}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={() => setContextMenu(null)}
        />
      )}
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
  );
};

export default Desktop;
